import { useEffect, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import {
  componentErrorText,
  componentFieldErrors,
  type PayabliComponentSuccess,
  type PayabliMode,
} from '@/lib/payments/payabli';
import {
  checkScanAmountWarnings,
  checkScanWarnings,
  isBusinessCheck,
  parseCheckScan,
  priceScannedCheck,
  type CheckScanRead,
} from '@/lib/payments/checkScanOcr';
import type { PaymentMethodDiscountSettings } from '@/lib/payments/paymentMethodDiscount';
import {
  chequeScanIdempotencyKey,
  releaseChequeScanIdempotencyKey,
  useClaimChequeScan,
  useConfirmChequeScan,
  useFailChequeScan,
  useInvalidateScanTarget,
  type ChequeScanClaim,
  type ChequeScanSettlement,
  type ChequeScanTarget,
} from '@/hooks/payments/useChequeScan';
import { isApiError } from '@/lib/api/errors';
import {
  canUseCamera,
  checkCaptureCancelled,
  interceptCheckCapture,
  type CheckCaptureRequest,
  type CheckCaptureResult,
} from '@/lib/payments/payabliCheckCapture';
import { usePayabliEmbed } from './usePayabliEmbed';
import { CheckCaptureChooser } from './CheckCaptureChooser';

/**
 * How long the component may take to answer `payabliExec('pay')`. It calls
 * `MoneyIn/getpaid` itself, so no answer is NOT "no debit": past this the scan
 * is left pending for the server's orphan sweep to settle from the ledger.
 */
const PAY_TIMEOUT_MS = 60_000;

/** Said when the check is for more than the invoice owes. */
export const OVER_BALANCE_MESSAGE =
  "A scanned check can't pay more than the invoice owes — record it as a check payment instead.";

/** The product's words for a business check (BOC is consumer-only). */
export const BUSINESS_CHECK_MESSAGE =
  "Business checks can't be scanned — record it as a check payment and deposit it at the bank.";

/** Said when the check is for more than the estimate's deposit. */
export const OVER_DEPOSIT_MESSAGE =
  "A scanned check can't pay more than the deposit — record it as a check payment instead.";

/** Said when the check is for more than what is left of a job's deposit. */
export const OVER_DEPOSIT_DUE_MESSAGE =
  "A scanned check can't pay more than the deposit due. Record it by hand instead: it pays the deposit, and the rest goes to the job.";

/** Said when the check does not cover the whole deposit. */
export const SHORT_OF_DEPOSIT_MESSAGE =
  'A scanned check has to cover the whole deposit — collect the deposit another way.';

/** The words that name what the check pays, per target. */
const TARGET_COPY = {
  invoice: {
    overTitle: 'More than the invoice owes',
    over: (face: string, due: string) =>
      `The check is for ${face}; the invoice owes ${due}. ${OVER_BALANCE_MESSAGE}`,
    overAction: 'Record as a check payment instead',
    checkBeforeCollecting: "Check the invoice's payments before collecting again.",
    customerChangedReason: "The invoice's customer changed while the check was scanned",
    customerChanged:
      "The invoice's customer changed while the check was being scanned. Nothing was charged — close this and open Record payment again.",
    reviewFallback: 'Check the invoice payments for this deposit.',
  },
  estimate_deposit: {
    overTitle: 'More than the deposit',
    over: (face: string, due: string) =>
      `The check is for ${face}; the deposit is ${due}. ${OVER_DEPOSIT_MESSAGE}`,
    overAction: 'Record as a check payment instead',
    checkBeforeCollecting: "Check the estimate's deposit before collecting again.",
    customerChangedReason: "The estimate's customer changed while the check was scanned",
    customerChanged:
      "The estimate's customer changed while the check was being scanned. Nothing was charged — close this and open Collect deposit again.",
    reviewFallback: "Check the estimate's deposit for this payment.",
  },
  job_deposit: {
    overTitle: 'More than the deposit due',
    over: (face: string, due: string) =>
      `The check is for ${face}; the deposit due is ${due}. ${OVER_DEPOSIT_DUE_MESSAGE}`,
    overAction: 'Record the check by hand',
    checkBeforeCollecting: "Check the job's payments before collecting again.",
    customerChangedReason: "The job's customer changed while the check was scanned",
    customerChanged:
      "The job's customer changed while the check was being scanned. Nothing was charged — close this and open Record payment again.",
    reviewFallback: "Check the job's payments for this deposit.",
  },
} as const;

/** What staff attest to. The server stores its own copy of the statement. */
export const CUSTOMER_NOTICE_TEXT =
  'The customer is here in person, this is their personal check, and they were told it will be converted to an electronic debit (and that they could take it back instead). They did not opt out. Keep the paper check secure for 14 days, then destroy it.';

export interface PayabliCheckScanConfig {
  publicToken: string;
  entryPoint: string;
  mode: PayabliMode;
}

export interface CheckScanOutcome extends ChequeScanSettlement {
  checkNumber: string;
  /** What the check was deposited for — its face amount. */
  amount: number;
}

/** The read as the form keeps it on screen: never the full account number. */
type ShownRead = Omit<CheckScanRead, 'accountNumber'>;

/** A press of Front or Back in the scanner, waiting on the chooser. */
type PendingCapture = CheckCaptureRequest & {
  id: number;
  resolve: (result: CheckCaptureResult | Promise<CheckCaptureResult>) => void;
  reject: (error: Error) => void;
};

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function str(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function messageOf(error: unknown, fallback: string): string {
  if (isApiError(error) || error instanceof Error) return error.message || fallback;
  return fallback;
}

/**
 * What the check pays: an invoice (`invoiceId`, `balanceDue`), an estimate's
 * deposit (`estimateId`, `depositAmount`), or a part of a job's deposit
 * (`jobId`, `depositDue`). An estimate's deposit is taken whole — a check for
 * less is refused here, as the Collect deposit dialog records nothing but the
 * full deposit. A job's is taken in parts, like an invoice's balance.
 */
type CheckScanTargetProps =
  | {
      invoiceId: string;
      /** What the invoice owes; null when the viewer may not see it. */
      balanceDue: number | null;
    }
  | {
      estimateId: string;
      /** The estimate's deposit, which the check has to cover. */
      depositAmount: number;
    }
  | {
      jobId: string;
      /** What is left of the job's deposit: the most the check may pay. */
      depositDue: number;
    };

function scanTargetOf(props: CheckScanTargetProps): {
  target: ChequeScanTarget;
  targetId: string;
  amountDue: number | null;
} {
  if ('estimateId' in props) {
    return {
      target: { kind: 'estimate_deposit', estimateId: props.estimateId },
      targetId: props.estimateId,
      amountDue: props.depositAmount,
    };
  }
  if ('jobId' in props) {
    return {
      target: { kind: 'job_deposit', jobId: props.jobId },
      targetId: props.jobId,
      amountDue: props.depositDue,
    };
  }
  return {
    target: { kind: 'invoice', invoiceId: props.invoiceId },
    targetId: props.invoiceId,
    amountDue: props.balanceDue,
  };
}

/**
 * Scan a customer's paper check with Payabli's embedded component (remote
 * deposit capture) and deposit it against an invoice, an estimate's deposit, or
 * a part of a job's deposit.
 *
 * The component photographs the check, reads it, and — on `pay` — debits it
 * ITSELF. So the order is fixed: staff review the read and attest the notice,
 * the server claims a PENDING row (refusing a check it has already deposited),
 * the component debits, and the server settles the row from Payabli's ledger.
 * Nothing here is taken at the component's word, and nothing is failed on a
 * guess: a debit that may have happened is left for the server to find.
 *
 * The check is debited for its face amount and nothing else: the amount staff
 * typed into the component (which Payabli validates against the paper), or
 * failing that the reader's. The ACH/check discount is priced back from it the
 * way a hand-recorded check's is.
 *
 * The full account number is sent to the claim and nowhere else — not
 * rendered, not logged, not kept in state.
 */
export function PayabliCheckScanForm(
  props: CheckScanTargetProps & {
    /** The tenant's ACH/check discount, priced like a hand-recorded check. */
    discountSettings?: PaymentMethodDiscountSettings | null;
    /** The invoice's (or estimate's) customer: the component's
     *  `customerData.customerNumber`. */
    contactId?: string | null;
    config: PayabliCheckScanConfig;
    onDone: (outcome: CheckScanOutcome) => void;
    onCancel: () => void;
    /** Offered when the check cannot be scanned (a business check, an overpayment). */
    onRecordManually?: () => void;
    /**
     * Offered instead of `onRecordManually` when the check is for more than is
     * due. On a job's deposit the check, recorded by hand, pays the deposit
     * and the rest goes to the job. Handed the check as read, so that starts
     * from what is written on it.
     */
    onRecordOverBalanceManually?: (check: { amount: number; checkNumber: string }) => void;
    /** Hosting dialogs must not close while a deposit is in flight. */
    onSubmittingChange?: (submitting: boolean) => void;
    /**
     * True from the moment the component is told to debit the check until the
     * server has proved it was not debited. Hosting dialogs must not offer any
     * other way to collect while it is: the debit may still land.
     */
    onDebitDispatchedChange?: (dispatched: boolean) => void;
    /**
     * Why this check may not be deposited right now, set by the host — money
     * already in flight that covers what is owed, phrased for an operator
     * ("$500.00 pending ACH"). Null when there is no such reason.
     *
     * Refusing the deposit rather than unmounting the form: nothing has moved
     * at this point, and Record manually and Cancel stay open. A debit this
     * form has ALREADY dispatched is a different case, owned by
     * `debitDispatched`, which must not be interrupted.
     */
    blockedReason?: string | null;
  },
) {
  const {
    discountSettings,
    contactId,
    config,
    onDone,
    onCancel,
    onRecordManually,
    onRecordOverBalanceManually = onRecordManually,
    onSubmittingChange,
    onDebitDispatchedChange,
    blockedReason = null,
  } = props;
  /** What the check is priced against: the invoice's balance, the estimate's
   *  deposit, or what is left of the job's. */
  const { target, targetId, amountDue } = scanTargetOf(props);
  const copy = TARGET_COPY[target.kind];
  const claimScan = useClaimChequeScan();
  const confirmScan = useConfirmChequeScan();
  const failScan = useFailChequeScan();

  const [read, setRead] = useState<ShownRead | null>(null);
  // The full account number lives here only until the claim is sent.
  const accountNumberRef = useRef<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [amountConfirmed, setAmountConfirmed] = useState(false);
  const [noticeConfirmed, setNoticeConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [debitDispatched, setDebitDispatched] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [businessRefused, setBusinessRefused] = useState(false);
  const [outcome, setOutcome] = useState<CheckScanOutcome | null>(null);
  /** A deposit that may have gone through but is not confirmed: no re-scan. */
  const [unresolved, setUnresolved] = useState<string | null>(null);
  /** A debit Payabli reported whose confirm did not get through. */
  const [confirmRetry, setConfirmRetry] = useState<{
    claim: ChequeScanClaim;
    transId: string;
  } | null>(null);

  /**
   * The claim the component is debiting, and the mount debiting it. Kept until
   * the server answers for the debit — through a timeout or an error — so a
   * success that arrives late is still confirmed.
   */
  const payingRef = useRef<{ token: object; claim: ChequeScanClaim } | null>(null);
  const payTimeoutRef = useRef<number | null>(null);

  /** The side of the check the scanner is waiting on a photo of. */
  const [capture, setCapture] = useState<PendingCapture | null>(null);
  const captureRef = useRef<PendingCapture | null>(null);
  const captureSeqRef = useRef(0);

  const customerNumber = contactId?.trim() || null;

  const onSubmittingChangeRef = useRef(onSubmittingChange);
  onSubmittingChangeRef.current = onSubmittingChange;
  const onDebitDispatchedChangeRef = useRef(onDebitDispatchedChange);
  onDebitDispatchedChangeRef.current = onDebitDispatchedChange;
  useEffect(() => {
    onSubmittingChangeRef.current?.(submitting);
  }, [submitting]);
  useEffect(() => {
    onDebitDispatchedChangeRef.current?.(debitDispatched);
  }, [debitDispatched]);

  // Closed on a debit not yet proved either way. Its claim already holds that
  // part of what is owed on the server, so the dialog's figures are refetched
  // to show it clearing: reopening must not offer it as still due.
  const invalidateScanTarget = useInvalidateScanTarget();
  const refreshOnUnmountRef = useRef<(() => void) | null>(null);
  refreshOnUnmountRef.current = debitDispatched ? () => void invalidateScanTarget(target) : null;
  useEffect(() => () => refreshOnUnmountRef.current?.(), []);

  function setBusy(next: boolean) {
    submittingRef.current = next;
    setSubmitting(next);
  }

  function clearPayTimeout() {
    if (payTimeoutRef.current !== null) {
      window.clearTimeout(payTimeoutRef.current);
      payTimeoutRef.current = null;
    }
  }

  function resetRead() {
    accountNumberRef.current = null;
    setRead(null);
    setReadError(null);
    setAmountConfirmed(false);
    setBusinessRefused(false);
  }

  // ── component callbacks (read through a ref: built once per mount) ──────

  function handleRead(payload: unknown) {
    // A re-read while a deposit is in flight describes nothing being charged.
    if (submittingRef.current || payingRef.current) return;
    const parsed = parseCheckScan(payload);
    if (parsed.kind === 'none') return;
    if (parsed.kind === 'incomplete') {
      resetRead();
      setReadError(
        `The check couldn't be read completely (no ${parsed.missing.join(', ')}). Retake the photos with all four corners in view.`,
      );
      return;
    }
    const { accountNumber, ...shown } = parsed.read;
    accountNumberRef.current = accountNumber;
    setRead(shown);
    setReadError(null);
    setErrorMessage(null);
    setAmountConfirmed(false);
    setBusinessRefused(false);
  }

  /**
   * Cancel a claim no debit is known to be behind. The server proves it on the
   * ledger first, and only a FAILED answer frees the paper (and this dialog).
   */
  async function failClaim(
    claim: ChequeScanClaim,
    reason: string,
    options: { message?: string; rescan?: boolean; keepRead?: boolean } = {},
  ) {
    try {
      const settlement = await failScan.mutateAsync({
        target,
        paymentId: claim.paymentId,
        reason,
      });
      releaseChequeScanIdempotencyKey(claim.idempotencyKey);
      if (settlement.status === 'FAILED') {
        // Proved never charged: the paper is free to be scanned again.
        setDebitDispatched(false);
        setErrorMessage(
          options.message ?? `${reason}. Nothing was charged — scan the check again.`,
        );
        if (!options.keepRead) resetRead();
        if (options.rescan !== false) embed.retry();
      } else {
        // The ledger shows a debit after all: that is the outcome.
        setOutcome({ ...settlement, checkNumber: claim.checkNumber, amount: claim.amount });
      }
    } catch (error) {
      // Refused — held for review, or Payabli could not be asked. The scan
      // stays open for the server; scanning the paper again is not safe.
      setUnresolved(messageOf(error, 'This check scan could not be cancelled.'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDebit(claim: ChequeScanClaim, transId: string) {
    setConfirmRetry(null);
    try {
      const settlement = await confirmScan.mutateAsync({
        target,
        paymentId: claim.paymentId,
        paymentTransId: transId,
      });
      payingRef.current = null;
      releaseChequeScanIdempotencyKey(claim.idempotencyKey);
      setUnresolved(null);
      setOutcome({ ...settlement, checkNumber: claim.checkNumber, amount: claim.amount });
    } catch (error) {
      // The check WAS debited; only the confirm failed. Offer to try again —
      // and the server's orphan sweep settles it if nobody does.
      setConfirmRetry({ claim, transId });
      setErrorMessage(messageOf(error, 'The deposit could not be confirmed.'));
    } finally {
      setBusy(false);
    }
  }

  /** The deposit's outcome is not known: say so, and leave it to the server. */
  function leaveUnresolved(message: string) {
    clearPayTimeout();
    setUnresolved(
      `${message} Don't scan this check again — Notifi will match the deposit with Payabli within a few minutes. ${copy.checkBeforeCollecting}`,
    );
    setBusy(false);
  }

  function handlePaid(token: object, result: PayabliComponentSuccess) {
    const paying = payingRef.current;
    // Nothing of ours is waiting on this mount's debit.
    if (!paying || paying.token !== token) return;
    clearPayTimeout();
    const data = (result.responseData ?? {}) as Record<string, unknown>;
    const transId =
      str(data.referenceId) ?? str(result.referenceId) ?? str(data.paymentTransId) ?? null;
    const resultCode = data.resultCode ?? result.resultCode;
    const approved =
      result.isSuccess !== false &&
      (resultCode === undefined || resultCode === null || Number(resultCode) === 1);
    if (approved && transId) {
      void confirmDebit(paying.claim, transId);
      return;
    }
    if (!approved) {
      // An explicit decline — the one component answer that says no debit
      // was made. Still proved on the ledger before the paper is freed.
      payingRef.current = null;
      void failClaim(paying.claim, componentErrorText(result, 'Payabli did not accept the check'));
      return;
    }
    // Approved with no transaction id: there is a debit we cannot name. The
    // server finds it on the ledger; failing the claim here would free paper
    // that has been charged.
    leaveUnresolved("Payabli accepted the check but didn't say which transaction it was.");
  }

  function handleComponentError(token: object, isCurrent: () => boolean, error: unknown) {
    const paying = payingRef.current;
    if (paying && paying.token === token) {
      const fieldErrors = componentFieldErrors(error);
      if (fieldErrors) {
        // The component refused its own form and sent nothing: cancel the
        // claim (the server still proves it on the ledger) and keep the read,
        // so staff fix the field and deposit again without a rescan.
        clearPayTimeout();
        payingRef.current = null;
        const text = fieldErrors.join('. ');
        void failClaim(paying.claim, text, {
          message: `${text}. Nothing was charged — correct it in the form above and deposit again.`,
          rescan: false,
          keepRead: true,
        });
        return;
      }
      // During the debit an error is NOT a decline: a dropped connection after
      // Payabli charged the check looks exactly like this. Treated like no
      // answer at all — the claim stays open, a late success is still
      // confirmed, and the sweep settles it from the ledger otherwise.
      leaveUnresolved(
        `${componentErrorText(error, 'The check reader reported a problem')} — it may still have been deposited.`,
      );
      return;
    }
    // A reader that has been replaced says nothing about the one on screen.
    if (!isCurrent() || submittingRef.current || payingRef.current) return;
    resetRead();
    setReadError(
      componentErrorText(
        error,
        "Payabli couldn't read the check. Retake the photos with all four corners in view",
      ),
    );
  }

  // ── photo source (the scanner asks; the chooser answers) ────────────────

  /**
   * The scanner wants a photo of one side. A second press while the first is
   * still open replaces it: the first is answered the way closing the camera
   * answers it.
   */
  function openCapture(request: CheckCaptureRequest): Promise<CheckCaptureResult> {
    return new Promise<CheckCaptureResult>((resolve, reject) => {
      captureRef.current?.reject(checkCaptureCancelled());
      captureSeqRef.current += 1;
      const pending: PendingCapture = { ...request, id: captureSeqRef.current, resolve, reject };
      captureRef.current = pending;
      setCapture(pending);
    });
  }

  function settleCapture(pending: PendingCapture, settle: (pending: PendingCapture) => void) {
    if (captureRef.current !== pending) return;
    captureRef.current = null;
    setCapture(null);
    settle(pending);
  }

  function dropCapture() {
    const pending = captureRef.current;
    if (!pending) return;
    captureRef.current = null;
    setCapture(null);
    pending.reject(checkCaptureCancelled());
  }

  const handlersRef = useRef({ handleRead, handlePaid, handleComponentError });
  handlersRef.current = { handleRead, handlePaid, handleComponentError };

  const embed = usePayabliEmbed({
    mode: config.mode,
    remountKey: JSON.stringify([config.publicToken, config.entryPoint, targetId, customerNumber]),
    onMount: () => {
      accountNumberRef.current = null;
      setRead(null);
      setReadError(null);
      setAmountConfirmed(false);
    },
    // Payabli's scanner only knows the camera: route its request for a photo
    // through a chooser that also takes one from disk. A bundle without that
    // method is left camera-only.
    onInstance: (instance) => {
      const restore = interceptCheckCapture(instance, openCapture);
      if (!restore) return;
      return () => {
        restore();
        dropCapture();
      };
    },
    buildConfig: ({ token, isCurrent, markRendered }) => ({
      type: 'methodEmbedded',
      token: config.publicToken,
      entryPoint: config.entryPoint,
      defaultOpen: 'rdc',
      // Remote deposit capture only. NOT `card: { enabled: false }` and the
      // like: the bundle refuses a config that switches both off ("'card' or
      // 'ach' should be enable") — leaving them out is what it accepts.
      // Input descriptors in the shape the card and bank rails take. Staff
      // type the check's amount into the component's CHECK AMOUNT, and
      // Payabli refuses a read whose amount does not match the paper.
      rdc: {
        enabled: true,
        inputs: {
          rdcAccountHolderName: { label: 'NAME ON CHECK', size: 6, row: 0, order: 0 },
          rdcAmount: { label: 'CHECK AMOUNT', size: 6, row: 0, order: 1 },
        },
      },
      ...(customerNumber ? { customerData: { customerNumber } } : {}),
      functionCallBackReady: (data: unknown) => {
        markRendered();
        if (!isCurrent()) return;
        handlersRef.current.handleRead(data);
      },
      functionCallBackSuccess: (result: PayabliComponentSuccess) => {
        // Not gated on `isCurrent`: a debit is a debit, and a remount after
        // it must not lose the confirm. `handlePaid` matches it to its claim.
        handlersRef.current.handlePaid(token, result);
      },
      functionCallBackError: (error: unknown) => {
        handlersRef.current.handleComponentError(token, isCurrent, error);
      },
    }),
  });

  // Gone with a deposit in flight: stop the timer, and never leave the host
  // believing one is still being submitted.
  useEffect(
    () => () => {
      clearPayTimeout();
      onSubmittingChangeRef.current?.(false);
    },
    [],
  );

  const pricing = read ? priceScannedCheck(read.faceAmount, amountDue, discountSettings) : null;
  const businessCheck = Boolean(read && isBusinessCheck(read)) || businessRefused;
  const overBalance = pricing?.kind === 'over_balance';
  // A deposit is taken whole: a check for less than it (after any discount it
  // earns) would leave the estimate reading collected on part of its deposit.
  const shortOfDeposit =
    target.kind === 'estimate_deposit' &&
    pricing !== null &&
    (pricing.kind === 'partial' ||
      (pricing.kind === 'over_discounted_due' &&
        Math.round(pricing.chargeAmount * 100) < Math.round((amountDue ?? 0) * 100)));
  const blocked = businessCheck || overBalance || shortOfDeposit;
  const warnings =
    read && pricing
      ? [
          ...checkScanAmountWarnings(
            pricing,
            target.kind === 'job_deposit' ? 'of the deposit will still be due' : undefined,
          ),
          ...checkScanWarnings(read),
        ]
      : [];
  const canDeposit =
    Boolean(read) &&
    !blocked &&
    blockedReason === null &&
    noticeConfirmed &&
    (warnings.length === 0 || amountConfirmed) &&
    !submitting &&
    !debitDispatched &&
    !unresolved &&
    !confirmRetry &&
    !outcome;

  async function handleDeposit() {
    if (submittingRef.current || !canDeposit || !read || !pricing) return;
    const accountNumber = accountNumberRef.current;
    const instance = embed.instanceRef.current;
    const token = embed.instanceTokenRef.current;
    if (!accountNumber) return;
    if (!instance || !token) {
      setErrorMessage('The check reader is not ready. Try again.');
      return;
    }
    setBusy(true);
    setErrorMessage(null);
    // The check's face amount, and nothing else, is what gets debited.
    const faceAmount = pricing.chargeAmount;
    const gross = pricing.grossAmount ?? undefined;
    const idempotencyKey = chequeScanIdempotencyKey(target, read.captureId, faceAmount, gross);
    let claim: ChequeScanClaim;
    try {
      claim = await claimScan.mutateAsync({
        target,
        body: {
          amount: faceAmount,
          idempotencyKey,
          captureId: read.captureId,
          routingNumber: read.routingNumber,
          accountNumber,
          checkNumber: read.checkNumber,
          ocr: {
            amount: read.ocrAmount,
            amountConfidence: read.amountConfidence,
            micrConfidence: read.micrConfidence,
            amountDiscrepancyDetected: read.amountDiscrepancyDetected,
            endorsementDetected: read.endorsementDetected,
            checkType: read.checkType,
          },
          customerNoticeConfirmed: true,
          ...(gross !== undefined ? { paymentMethodDiscountGrossAmount: gross } : {}),
        },
      });
    } catch (error) {
      if (
        isApiError(error) &&
        (error.data as { code?: unknown } | undefined)?.code === 'CHEQUE_SCAN_BUSINESS_CHECK'
      ) {
        setBusinessRefused(true);
      }
      // Verbatim — a duplicate check, a scan held for review. The key is
      // kept: a retry of this scan replays this claim, never opens another.
      setErrorMessage(messageOf(error, 'The check could not be deposited.'));
      setBusy(false);
      return;
    }

    // Every way out before `pay` cancels the claim: nothing has been debited.
    // The reader was replaced while the claim was in flight (a Retry): the
    // capture this claim names is gone with it.
    const live = embed.instanceRef.current;
    if (embed.instanceTokenRef.current !== token || !live) {
      await failClaim(claim, 'The check reader was reset before the check was charged');
      return;
    }
    // The row must be for exactly the check's amount: the component debits
    // what it is told, and it is told the claim's amount.
    if (Math.round(claim.amount * 100) !== Math.round(faceAmount * 100)) {
      await failClaim(claim, "The deposit didn't match the amount on the check", {
        rescan: false,
      });
      return;
    }
    // The component must send the customer the claim was opened for, or the
    // server will not match the debit to this row. Payabli's `updateConfig`
    // replaces the whole config (and the check with it), so a mismatch is not
    // patched over: the claim is cancelled and the dialog reopened.
    if (claim.customerNumber && claim.customerNumber !== customerNumber) {
      await failClaim(claim, copy.customerChangedReason, {
        message: copy.customerChanged,
        rescan: false,
      });
      return;
    }

    payingRef.current = { token, claim };
    setDebitDispatched(true);
    payTimeoutRef.current = window.setTimeout(() => {
      payTimeoutRef.current = null;
      // No answer is not "no debit". The claim stays open (and a late answer
      // is still confirmed); the server's sweep settles it from the ledger.
      leaveUnresolved("The check reader didn't respond.");
    }, PAY_TIMEOUT_MS);
    try {
      live.payabliExec('pay', {
        paymentDetails: { totalAmount: claim.amount, serviceFee: 0 },
      });
    } catch {
      // Refused before it left the page: nothing reached Payabli.
      clearPayTimeout();
      payingRef.current = null;
      await failClaim(claim, 'The check reader could not start the deposit');
    }
  }

  function handleCancel() {
    if (submittingRef.current) return;
    onCancel();
  }

  if (outcome) {
    const cleared = outcome.status === 'SUCCEEDED';
    const clearing =
      outcome.status === 'PENDING' &&
      (!outcome.detail || outcome.detail === 'The processor has not finished this payment yet');
    return (
      <div className="space-y-4" data-testid="check-scan-result">
        {cleared || clearing ? (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>
              {cleared
                ? 'Check deposited'
                : 'Check deposited — it will clear in a few business days'}
            </AlertTitle>
            <AlertDescription>Check #{outcome.checkNumber}</AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {outcome.status === 'FAILED' ? 'The deposit failed' : 'The deposit needs review'}
            </AlertTitle>
            <AlertDescription>{outcome.detail ?? copy.reviewFallback}</AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end">
          <Button type="button" onClick={() => onDone(outcome)}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative rounded-lg border border-border p-4">
        <div id={embed.containerId} />
        {capture ? (
          <CheckCaptureChooser
            key={capture.id}
            side={capture.side}
            cameraAvailable={canUseCamera()}
            onPhoto={(data) =>
              settleCapture(capture, (pending) => pending.resolve({ data, side: pending.side }))
            }
            onCamera={() =>
              settleCapture(capture, (pending) => {
                try {
                  pending.resolve(pending.openCamera());
                } catch (error) {
                  pending.reject(error instanceof Error ? error : checkCaptureCancelled());
                }
              })
            }
            onCancel={() =>
              settleCapture(capture, (pending) => pending.reject(checkCaptureCancelled()))
            }
          />
        ) : null}
        {!embed.isRendered && !embed.loadError ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : null}
      </div>

      {embed.loadError ? (
        <Alert variant="destructive" data-testid="payabli-load-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Check scanner unavailable</AlertTitle>
          <AlertDescription>
            <p>{embed.loadError}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              disabled={submitting || debitDispatched}
              onClick={embed.retry}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {readError ? (
        <Alert variant="destructive" data-testid="check-scan-read-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Check not read</AlertTitle>
          <AlertDescription>{readError}</AlertDescription>
        </Alert>
      ) : null}

      {read && pricing ? (
        <div
          className="space-y-3 rounded-lg border border-border p-4"
          data-testid="check-scan-review"
        >
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Check #</dt>
            <dd className="tabular-nums">{read.checkNumber}</dd>
            <dt className="text-muted-foreground">Routing</dt>
            <dd className="tabular-nums">{read.routingNumber}</dd>
            <dt className="text-muted-foreground">Account</dt>
            <dd className="tabular-nums">••{read.accountLast4}</dd>
            <dt className="text-muted-foreground">Check amount</dt>
            <dd className="tabular-nums font-medium" data-testid="check-scan-face-amount">
              {fmt(read.faceAmount)}
            </dd>
            {pricing.amountDue !== null ? (
              <>
                <dt className="text-muted-foreground">Amount due</dt>
                <dd className="tabular-nums">{fmt(pricing.amountDue)}</dd>
              </>
            ) : null}
            {pricing.discountAmount > 0 ? (
              <>
                <dt className="text-muted-foreground">ACH/check discount</dt>
                <dd className="tabular-nums" data-testid="check-scan-discount">
                  {fmt(pricing.discountAmount)}
                </dd>
              </>
            ) : null}
          </dl>

          {businessCheck ? (
            <Alert variant="destructive" data-testid="check-scan-business">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Business check</AlertTitle>
              <AlertDescription>
                <p>{BUSINESS_CHECK_MESSAGE}</p>
                {onRecordManually ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={onRecordManually}
                  >
                    Record as a check payment instead
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : overBalance ? (
            <Alert variant="destructive" data-testid="check-scan-over-balance">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{copy.overTitle}</AlertTitle>
              <AlertDescription>
                <p>{copy.over(fmt(read.faceAmount), fmt(pricing.amountDue ?? 0))}</p>
                {onRecordOverBalanceManually ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() =>
                      onRecordOverBalanceManually({
                        amount: read.faceAmount,
                        checkNumber: read.checkNumber,
                      })
                    }
                  >
                    {copy.overAction}
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : shortOfDeposit ? (
            <Alert variant="destructive" data-testid="check-scan-short-of-deposit">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Less than the deposit</AlertTitle>
              <AlertDescription>
                <p>
                  {`The check is for ${fmt(read.faceAmount)}; the deposit is ${fmt(amountDue ?? 0)}${
                    pricing.amountDue !== null && pricing.amountDue < (amountDue ?? 0)
                      ? `, or ${fmt(pricing.amountDue)} with the ACH/check discount`
                      : ''
                  }. ${SHORT_OF_DEPOSIT_MESSAGE}`}
                </p>
                {onRecordManually ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={onRecordManually}
                  >
                    Collect the deposit another way
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {warnings.length > 0 ? (
                <div
                  className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30"
                  data-testid="check-scan-warnings"
                >
                  <ul className="list-disc space-y-1 pl-4 text-amber-800 dark:text-amber-300">
                    {warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                  <label className="flex items-start gap-2">
                    <Checkbox
                      checked={amountConfirmed}
                      disabled={submitting}
                      onCheckedChange={(checked) => setAmountConfirmed(checked === true)}
                      aria-label="I confirmed the amount on the check"
                      className="mt-0.5"
                    />
                    <span>I confirmed the amount on the check</span>
                  </label>
                </div>
              ) : null}
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={noticeConfirmed}
                  disabled={submitting}
                  onCheckedChange={(checked) => setNoticeConfirmed(checked === true)}
                  aria-label="Customer notice given"
                  className="mt-0.5"
                />
                <span>{CUSTOMER_NOTICE_TEXT}</span>
              </label>
            </>
          )}
        </div>
      ) : null}

      {unresolved ? (
        <Alert variant="destructive" data-testid="check-scan-unresolved">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Deposit not confirmed</AlertTitle>
          <AlertDescription>{unresolved}</AlertDescription>
        </Alert>
      ) : null}

      {errorMessage ? (
        <Alert variant="destructive" data-testid="check-scan-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Check not deposited</AlertTitle>
          <AlertDescription>
            <p>{errorMessage}</p>
            {confirmRetry ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={submitting}
                onClick={() => {
                  setBusy(true);
                  setErrorMessage(null);
                  void confirmDebit(confirmRetry.claim, confirmRetry.transId);
                }}
              >
                Try confirming again
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {blockedReason !== null ? (
        <p role="status" className="text-sm text-muted-foreground">
          {blockedReason} covers this balance. Deposit this check only if that payment is returned
          or fails.
        </p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={submitting} onClick={handleCancel}>
          Cancel
        </Button>
        {blocked ? null : (
          <Button type="button" disabled={!canDeposit} onClick={() => void handleDeposit()}>
            {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Deposit check
          </Button>
        )}
      </div>
    </div>
  );
}
